class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.inputWrap = this.querySelector(".predictive-search_input-wrap");
    this.overlay = document.querySelector(".header_overlay");

    this.filterQuery = null;

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  onChange() {}
}

customElements.define("search-form", SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    // Set when the shopper dismisses the panel, so a request that was already
    // in flight cannot re-open what they just closed.
    this.userDismissed = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.select = this.querySelector("select");
    this.filterSpan = this.querySelector(
      ".predictive-search_filter-label span"
    );
    this.searchTermSpan = null;
    // Persisted view mode for the results ("list" or "grid")
    this.resultView = "list";

    this.setupEventListeners();
  }

  setupEventListeners() {
    // The header search has no category <select> — it was dropped in the
    // redesign — so this ran on a null and threw on every page load, aborting
    // this method at its second statement. Everything below it (focus,
    // focusout, resize, scroll and the outside-click dismissal) was therefore
    // never bound, which is why the results panel opened but nothing could
    // ever close it. Each optional element is now checked before use.
    if (this.input && this.input.form) {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    }
    if (this.select) {
      this.select.addEventListener("change", this.onFilterSelect.bind(this));
    }
    if (this.input) {
      this.input.addEventListener("focus", this.onFocus.bind(this));
    }
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    // focusout alone was the ONLY dismissal path, and it depends on focus
    // actually moving. Pressing a non-focusable part of the page (a heading,
    // blank space, an image) does not reliably move focus, so the panel stayed
    // open. Close on any press that lands outside this element.
    //
    // Read the panel state from the DOM rather than the isOpen flag: if that
    // flag ever falls out of step with what is on screen, a flag-based guard
    // silently refuses to close and the panel is stuck for good.
    this._isPanelVisible = () => {
      if (this.hasAttribute("open")) return true;
      const panel = this.predictiveSearchResults;
      return !!panel && panel.style.display !== "none";
    };
    this._onDocumentDismiss = (event) => {
      if (!this._isPanelVisible()) return;
      const target = event.target;
      // The dimmed backdrop (.overlay-search) is rendered INSIDE this element
      // by the results markup, so a plain contains() check counts a press on it
      // as a press on the panel. It is a backdrop — pressing it should dismiss,
      // exactly like pressing the page behind it.
      const onBackdrop =
        target && target.closest && target.closest(".overlay-search");
      if (!onBackdrop && target && this.contains(target)) return;
      this.userDismissed = true;
      this.close();
    };
    // Capture phase, so a stopPropagation() anywhere else on the page cannot
    // swallow the dismissal. Both events are bound because a press that ends
    // outside the panel should close it whichever one the browser delivers.
    document.addEventListener("pointerdown", this._onDocumentDismiss, true);
    document.addEventListener("click", this._onDocumentDismiss, true);
    this._onDocumentKeydown = (event) => {
      if (event.key === "Escape" && this._isPanelVisible()) {
        this.userDismissed = true;
        this.close();
      }
    };
    document.addEventListener("keydown", this._onDocumentKeydown, true);
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    window.addEventListener("resize", () => {
      if (this.isOpen) this.schedulePosition();
    });
    window.addEventListener(
      "scroll",
      () => {
        if (this.isOpen) this.schedulePosition();
      },
      { passive: true }
    );
  }

  // Throttle repositioning to one write per animation frame. Prevents the
  // per-scroll-event style thrash that causes flicker on mobile (and while
  // the iOS address bar animates in/out during scroll).
  schedulePosition() {
    if (this._posRaf) return;
    this._posRaf = requestAnimationFrame(() => {
      this._posRaf = null;
      this.positionResults();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onFilterSelect(event) {
    this.filterQuery = event.target.selectedOptions[0].value;

    if (this.filterQuery === "*") {
      if (this.filterSpan)
        this.filterSpan.innerText = event.target.selectedOptions[0].innerText;
    } else {
      if (this.filterSpan) this.filterSpan.innerText = event.target.value;
    }
    this.getSearchResults(this.searchTerm, this.filterQuery);
  }

  onChange() {
    this.userDismissed = false;
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm, this.filterQuery);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.userDismissed = false;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );

    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(
        previousTerm,
        "laskldjlsk"
      );
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm, productType, queryOverride) {
    const queryKey = `${searchTerm}-${productType || ""}`
      .toLowerCase()
      .replace(" ", "-");
    const term = queryOverride ? queryOverride.query : searchTerm;
    let filterQuery = `${encodeURIComponent(term)}`;

    if (productType) {
      filterQuery = `product_type:${productType} AND ${encodeURIComponent(
        term
      )}`;
    } else {
      filterQuery = encodeURIComponent(term);
    }

    if (!queryOverride && this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const oem = window.FourPhOemSearch;
    const exactLookup =
      !queryOverride && oem && oem.isCode(searchTerm) ? oem.exactMatches(searchTerm) : null;

    fetch(
      `${routes.predictive_search_url}?q=${filterQuery}&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku,tag&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        let resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        if (queryOverride) {
          const esc = (s) =>
            s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          resultsMarkup = resultsMarkup
            .split(esc(queryOverride.query)).join(esc(searchTerm))
            .split(queryOverride.query).join(esc(searchTerm));
          resultsMarkup = oem.pruneDropdown(resultsMarkup, queryOverride.handles, searchTerm);
        }

        const finish = () => {
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          this.renderSearchResults(resultsMarkup, searchTerm);
        };

        if (exactLookup) {
          return exactLookup.then((match) => {
            if (match && this.searchTerm === searchTerm) {
              this.getSearchResults(searchTerm, productType, match);
            } else {
              finish();
            }
          });
        }

        finish();
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    // Footer CTA label is rendered server-side per search term; keep a
    // reference only for keyboard navigation/legacy safety.
    this.searchTermSpan = this.querySelector(
      "#data-predictive-search-search-for-text"
    );

    if (this.searchTerm.length == 0) {
      this.querySelector(".predictive-search_footer")?.classList.add("hidden");
    }

    // Wire up the category tabs + list/grid view toggle for the fresh markup.
    this.bindResultUI();

    // This runs when a request comes back, which can be well after the shopper
    // clicked away — opening unconditionally here re-opened the panel a moment
    // after every dismissal, which is why clicking outside looked like it never
    // worked. Only surface results if they are still in the search box.
    if (this.userDismissed || !this.contains(document.activeElement)) return;

    this.open();
  }

  /* ----- Results UI: category tabs + list/grid view toggle ----- */

  bindResultUI() {
    const productsWrap = this.querySelector("[data-products-wrap]");
    if (!productsWrap) return;

    // Re-apply the persisted view mode to the freshly rendered markup.
    this.applyView(this.resultView || "list");

    const viewButtons = this.querySelectorAll("[data-view-toggle] [data-view]");
    viewButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.applyView(btn.dataset.view));
    });

    const tabs = this.querySelectorAll(
      "[data-search-tabs] .predictive-search_tab"
    );
    tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.applyTabFilter(tab));
    });

    // Wishlist heart – visual toggle only (no wishlist backend yet).
    const hearts = this.querySelectorAll(".predictive-search_wishlist");
    hearts.forEach((heart) => {
      heart.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const active = heart.getAttribute("aria-pressed") === "true";
        heart.setAttribute("aria-pressed", active ? "false" : "true");
        heart.classList.toggle("is--active", !active);
      });
    });
  }

  applyView(view) {
    this.resultView = view;

    const wrap = this.querySelector("[data-products-wrap]");
    if (wrap) wrap.classList.toggle("is--grid-view", view === "grid");

    // Highlight the active icon in the segmented toggle.
    const buttons = this.querySelectorAll("[data-view-toggle] [data-view]");
    buttons.forEach((btn) => {
      const active = btn.dataset.view === view;
      btn.classList.toggle("is--active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  applyTabFilter(tab) {
    const type = tab.dataset.filterType;
    const container = tab.closest("[data-products-wrap]");
    if (!container) return;

    container.querySelectorAll(".predictive-search_tab").forEach((t) => {
      const active = t === tab;
      t.classList.toggle("is--active", active);
      t.setAttribute("aria-selected", active ? "true" : "false");
    });

    const items = container.querySelectorAll(
      ".predictive-search_item[data-product-type]"
    );
    let visible = 0;
    items.forEach((li) => {
      const show = type === "*" || li.dataset.productType === type;
      li.hidden = !show;
      if (show) visible += 1;
    });

    const empty = container.querySelector("[data-tab-empty]");
    if (empty) empty.classList.toggle("hidden", visible > 0);
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.predictiveSearchResults.style.display = "block";
    if (this.inputWrap) this.inputWrap.style.borderRadius = "6px 6px 0px 0px";
    // Open as an inline dropdown anchored under the search bar (not a modal),
    // so no dark page overlay. Recompute the desktop wide-panel width fresh.
    this._deskApplied = false;
    this._deskKey = "";
    this.positionResults();
  }

  // Vertical positioning is handled in CSS (the panel is absolutely anchored
  // under the search bar and scrolls WITH the page — no fixed/JS repositioning
  // on scroll, so no blink on desktop or iOS).
  //
  // Horizontally, on DESKTOP we widen the panel beyond the search bar so 4 grid
  // cards fit (the search bar alone is only wide enough for 3). The width/left
  // are computed on open + resize only; during scroll the sticky search bar's
  // horizontal position doesn't change, so change-detection skips every rewrite
  // → still no blink. On mobile/tablet the panel keeps the CSS width (= search
  // bar width).
  positionResults() {
    const render = this.querySelector(".predictive-search_render");
    if (!render || !this.inputWrap) return;

    const isDesktop = window.matchMedia("(min-width: 992px)").matches;
    if (!isDesktop) {
      if (this._deskApplied) {
        render.style.width = "";
        render.style.left = "";
        render.style.right = "";
        this._deskApplied = false;
        this._deskKey = "";
      }
      return;
    }

    const rect = this.inputWrap.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const margin = 16;
    // Target the Figma panel width (1000px); never exceed the viewport.
    // Centre on the HEADER CONTAINER, not on the search bar. The bar now sits
    // well left of centre (it aligns with the menu row below), so centring on
    // it pushed the panel visibly to one side of the page. Cap to the
    // container too, so the panel never sticks out past the header content.
    const container = this.closest(".header_wrap") || this.closest(".u-container");
    const cRect = container ? container.getBoundingClientRect() : null;
    const available = cRect
      ? Math.min(cRect.width, vw - margin * 2)
      : vw - margin * 2;
    const width = Math.min(1000, available);
    const anchorCenter = cRect
      ? cRect.left + cRect.width / 2
      : rect.left + rect.width / 2;
    let panelLeftVp = anchorCenter - width / 2;
    if (panelLeftVp < margin) panelLeftVp = margin;
    if (panelLeftVp + width > vw - margin) panelLeftVp = vw - margin - width;
    // Convert the viewport-left back to coordinates relative to the search bar
    // (the panel's absolute containing block starts at the search bar's left).
    const left = Math.round(panelLeftVp - rect.left);
    const key = width + "|" + left;
    if (this._deskApplied && this._deskKey === key) return;
    this._deskKey = key;
    this._deskApplied = true;
    render.style.right = "auto";
    render.style.width = width + "px";
    render.style.left = left + "px";
  }

  disconnectedCallback() {
    document.removeEventListener("pointerdown", this._onDocumentDismiss, true);
    document.removeEventListener("click", this._onDocumentDismiss, true);
    document.removeEventListener("keydown", this._onDocumentKeydown, true);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if (this.inputWrap) this.inputWrap.style.borderRadius = "";
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.style.display = "none";
  }
}

customElements.define("predictive-search", PredictiveSearch);
